import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { Dispatch, SetStateAction, useEffect } from "react";
import { last, throttle } from "lodash";
import SwiperCore from "swiper";

export const getShareLinkUrl = (
  data: PublicBaseEventSerializer | PublicMembershipGroupSerializer
): string => data.frontend_details_url;

export const getShareLinkDescription = (
  data: PublicBaseEventSerializer | PublicMembershipGroupSerializer
): string => {
  let description = "";

  if (data?.name && data?.venue?.name) {
    description = `Visit ${data.name} at ${data.venue.name}`;
  }

  return description;
};

export type TabOption = {
  id: number;
  title?: string;
  showTab: boolean;
  hash?: string;
  openModal?: () => void;
};

const THROTTLE_TIME = 200;
const EDGE_THRESHOLD = 150;

export interface useSyncTabWithScrollProps {
  visibleTabs: TabOption[];
  selectedTab: number;
  swiper?: SwiperCore;
  setSelectedTab: Dispatch<SetStateAction<number>>;
}

export const useSyncTabWithScroll = ({
  visibleTabs,
  selectedTab,
  swiper,
  setSelectedTab,
}: useSyncTabWithScrollProps) => {
  useEffect(() => {
    const handleScroll = throttle(() => {
      const ids = visibleTabs
        .map((tab) => tab.hash && `#${CSS.escape(tab.hash)}`)
        .filter(Boolean)
        .join(",");
      const tabElements = Array.from(
        document.querySelectorAll(ids)
      ) as HTMLElement[];
      const isAtBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - EDGE_THRESHOLD;
      let visibleElement: HTMLElement | undefined;

      // If at bottom of page, select the last tab
      if (isAtBottom && tabElements.length > 0) {
        visibleElement = last(tabElements) as HTMLElement;
      } else {
        // Find the element whose top is closest to (but at or above) the threshold from the top
        // This selects the section that has scrolled into view
        visibleElement = tabElements.reduce<HTMLElement | undefined>(
          (best, element) => {
            const rect = element.getBoundingClientRect();
            // Element's top should be at or above the threshold (scrolled into view)
            if (rect.top <= EDGE_THRESHOLD) {
              // If no best yet, or this element's top is higher (closer to threshold)
              if (!best) return element;
              const bestRect = best.getBoundingClientRect();
              // Prefer the element with the higher (closer to 0) top position
              if (rect.top > bestRect.top) return element;
            }
            return best;
          },
          undefined
        );

        // Fallback: if no element has scrolled past threshold, use first visible element
        if (!visibleElement && tabElements.length > 0) {
          visibleElement = tabElements.find((element) => {
            const rect = element.getBoundingClientRect();
            return rect.bottom > 0;
          });
        }
      }

      if (visibleElement) {
        const { id } = visibleElement;
        // Find the tab that matches the visible element's hash
        const visibleTabId =
          visibleTabs.find((tab) => tab.hash === id)?.id ?? -1;

        // Only update if the visible tab is different from the selected tab
        if (visibleTabId !== -1 && visibleTabId !== selectedTab) {
          setSelectedTab(visibleTabId);

          if (swiper) {
            swiper.slideTo(visibleTabId);
          }
        }
      }
    }, THROTTLE_TIME);

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleTabs, selectedTab, setSelectedTab, swiper]);
};
